<template>
  <ai-carousel-with-description
    id="HotelWelcome"
    v-model="currentStep"
    access-id="HotelCarouselWelcome"
    :items="items"
    mobile-button
    :title="$t('hotel.welcome.title', { name })"
    :description="description ?? ''"
    :gutter="gutter"
    :slide-width="slideWidth"
    :aria-label="$t('hotel.welcome.title', { name })"
    gallery-on-carousel-click>
    <div class="HotelCarouselWelcome-hoursContent">
      <div class="HotelCarouselWelcome-hoursItem">
        <ai-icon
          class="HotelCarouselWelcome-hoursIcon"
          name="check-in"
          :size="24" />
        <ai-typo as="span" variant="paragraph-04-regular">
          {{ $t('hotel.welcome.checkIn', { hour: checkInLabelFormatLabel }) }}
        </ai-typo>
      </div>
      <div class="HotelCarouselWelcome-hoursItem">
        <ai-icon
          class="HotelCarouselWelcome-hoursIcon"
          name="check-out"
          :size="24" />
        <ai-typo as="span" variant="paragraph-04-regular">
          {{
            $t('hotel.welcome.checkOut', {
              hour: checkOutFormatLabel,
            })
          }}
        </ai-typo>
      </div>
    </div>
    <div v-if="map" class="HotelCarouselWelcome-mapContent">
      <ai-button
        :url="map"
        as-link
        :label="$t('hotel.welcome.mapButtonLabel')"
        variant="secondary"
        :aria-label="$t('hotel.welcome.mapButtonLabel')"
        target="_blank" />
    </div>
  </ai-carousel-with-description>
</template>

<script setup lang="ts">
interface Props {
  images: AiImageProps[];
  name?: string;
  description?: string;
  checkInLabel?: string;
  checkOutLabel?: string;
  map?: string;
}

const props = defineProps<Props>();
const items = props.images.map(image => ({ ...image, loading: 'lazy' }));

const { d } = useI18n();

const formatDate = (dateCheck: string) => {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

  if (!dateCheck || !timeRegex.test(dateCheck)) {
    return '';
  }

  const [hours, minutes] = dateCheck.split(':');
  const date = new Date(0, 0, 0, +hours, +minutes);

  return d(date, 'time');
};

const checkInLabelFormatLabel = computed(() =>
  formatDate(props.checkInLabel ?? ''),
);
const checkOutFormatLabel = computed(() =>
  formatDate(props.checkOutLabel ?? ''),
);

const isSmallDevice = useMatchMediaQuery('tablet');
const isDesktopDevice = useMatchMediaQuery('large-desktop');

const gutter = computed(() => {
  if (isSmallDevice.value) return 16;

  return isDesktopDevice.value ? 64 : 32;
});
const slideWidth = computed(() => (isSmallDevice.value ? 0.9 : 1));

const currentStep = ref<number>(1);
</script>
<style scoped lang="scss">
@use '@/assets/styles/utilities/constants';
@use '@/assets/styles/utilities/mq';

.HotelCarouselWelcome-hoursContent {
  @media (mq.$from-medium) {
    display: flex;
    gap: constants.$margin-02;
  }
}

.HotelCarouselWelcome-hoursItem {
  display: flex;
  align-items: center;

  &:first-child {
    margin-bottom: constants.$margin-01;
  }
  @media (mq.$from-medium) {
    &:first-child {
      margin-bottom: 0;
    }
  }
}

.HotelCarouselWelcome-hoursIcon {
  margin-right: constants.$inner-02;
}

.HotelCarouselWelcome-mapContent {
  margin-top: constants.$margin-03;
}
</style>
